import { useDeferredValue } from "react";
import PropTypes from "prop-types";
import {
  ProgressCircle,
  TooltipTrigger,
  Tooltip,
  ActionButton,
} from "@react-spectrum/s2";

import CheckmarkCircle from "@react-spectrum/s2/icons/CheckmarkCircle";
import CloseCircle from "@react-spectrum/s2/icons/CloseCircle";
import RemoveCircle from "@react-spectrum/s2/icons/RemoveCircle";
import InfoCircle from "@react-spectrum/s2/icons/InfoCircle";

import { useLinkChecker } from "@app/hooks/linkChecker";

import {
  iconStyle,
  style,
} from "@react-spectrum/s2/style" with { type: "macro" };

export const LinkChecker = ({ url }) => {
  const deferredUrl = useDeferredValue(url);

  const linkChecker = useLinkChecker(deferredUrl);

  if (!url) {
    return null;
  }

  if (linkChecker.isPending) {
    return (
      <ProgressCircle
        isIndeterminate
        size="S"
        staticColor="auto"
        aria-label="Checking link..."
        UNSAFE_className={style({
          display: "inline-flex",
          verticalAlign: "middle",
          marginEnd: 4,
        })}
      />
    );
  }

  let icon;

  if (
    linkChecker.isError ||
    linkChecker.data?.status === 403 ||
    linkChecker.data?.error?.toLowerCase?.().startsWith?.("timeout")
  ) {
    icon = (
      <RemoveCircle
        styles={iconStyle({ color: "notice", size: "XS" })}
        UNSAFE_className={style({ verticalAlign: "middle", marginEnd: 4 })}
        aria-label="Link status is unknown"
      />
    );
  } else if (linkChecker.data?.isRedirect) {
    icon = (
      <InfoCircle
        styles={iconStyle({ color: "notice", size: "XS" })}
        UNSAFE_className={style({ verticalAlign: "middle", marginEnd: 4 })}
        aria-label="Link status is unknown"
      />
    );
  } else if (linkChecker.data?.exists && linkChecker.data?.status === 200) {
    icon = (
      <CheckmarkCircle
        styles={iconStyle({ color: "positive", size: "XS" })}
        UNSAFE_className={style({ verticalAlign: "middle", marginEnd: 4 })}
        aria-label="Link is valid"
      />
    );
  } else if (!linkChecker.data?.exists || linkChecker.data?.status >= 400) {
    icon = (
      <CloseCircle
        styles={iconStyle({ color: "negative", size: "XS" })}
        UNSAFE_className={style({ verticalAlign: "middle", marginEnd: 4 })}
        aria-label="Link is invalid"
      />
    );
  } else {
    return null;
  }

  let tooltip;

  if (linkChecker.data?.isRedirect) {
    tooltip = `Link redirects to ${linkChecker.data.redirectLocation}`;
  } else if (
    linkChecker.data?.status === 403 ||
    linkChecker.data?.error?.toLowerCase?.().startsWith?.("timeout")
  ) {
    tooltip = "Link status is unknown";
  } else if (linkChecker.data?.exists && linkChecker.data?.status === 200) {
    tooltip = "Link is valid";
  } else if (!linkChecker.data?.exists || linkChecker.data?.status >= 400) {
    tooltip = "Link is invalid";
  } else if (linkChecker.isError) {
    tooltip = "Link status is unknown";
  }

  return (
    <TooltipTrigger>
      <ActionButton
        isQuiet
        size="XS"
        aria-label={tooltip}
        UNSAFE_className={style({ display: "inline-flex" })}
      >
        {icon}
      </ActionButton>
      <Tooltip>{tooltip}</Tooltip>
    </TooltipTrigger>
  );
};

LinkChecker.propTypes = {
  url: PropTypes.string.isRequired,
};
